/**
 * a generic client side dialog
 */

import LoggingBase from "../../base/loggingbase";
import KeyHdl from "../../key/KeyHdl";
import DomEventHelper from "../../utils/DomEventHelper";
import HtmHelper from "../../utils/HtmHelper";
import Validator from "../../utils/Validator";
import ItmMgr from "../ItmMgr";
import Utils from '../../utils/Utils';

export default class ClientDialog extends LoggingBase {

    /**
     * constructs a new instance
     * @param {String} ln logger name 
     * @param {*} ico icon descriptor
     * @param {*} par additional parameters 
     */
    constructor(ln, ico, par) {
        super(ln || 'gui.misc.ClientDialog');
		const host = document.createElement('div');
		this._hostElm = host;
        this._keysBlocked = KeyHdl.getInstance().isBlocked();
        this._iconDsc = ico || {};
        this._dlgPar = par || {};
    }

    /**
     * @returns {HTMLElement} the dialog host element
     */
    get hostElm() {
        return this._hostElm;
    }

    /**
     * @returns {Boolean} the current "key handling is blocked" status
     */
    get keysBlocked() {
        return this._keysBlocked;
    }

    /**
     * @returns {*} the icon descriptor
     */
    get iconDsc() {
        return this._iconDsc;
    }

    /**
     * @returns {*} dialog parameters
     */
    get dlgPar() {
        return this._dlgPar;
    }

    /**
     * @override
     */
    doDestroy() {
        HtmHelper.rmvDomElm(this._hostElm);
        KeyHdl.getInstance().blockGlobalKeyHandling(this.keysBlocked);
        delete this._hostElm;
        delete this._keysBlocked;
        delete this._iconDsc;
        delete this._dlgPar;
        super.doDestroy();
    }

    /**
     * renders the dialog
     */
    render() {
        const im = ItmMgr.getInst();
        const host = this.hostElm;
        const par = this.dlgPar;
        host.className = 'dlghost';

		const dlg_wnd = document.createElement('div'); 
		dlg_wnd.className = 'dlgwindow';

		const dlg_body = document.createElement('div');
		dlg_body.className = 'dlgbody';

		const ctt_frame = document.createElement('div');
		ctt_frame.className = 'dlgcttframe';
		const hdr_frame = document.createElement('div');
		hdr_frame.className = 'dlghdrframe';

        if ( this.iconDsc ) {
			const hdr_icon = document.createElement('div');
			hdr_icon.className = 'dlghdricon';
			hdr_icon.appendChild(im.creImg(this.iconDsc, 24, false));
			hdr_frame.appendChild(hdr_icon);
        }
        if ( Validator.isString(par.ttl) ) {
            const ttl = document.createElement('div');
            ttl.className = 'dlghdrtitle';
            ttl.innerHTML = Utils.escHtml(par.ttl);
            hdr_frame.appendChild(ttl);
        }

        const body_frame = document.createElement('div');
        body_frame.className = 'dlgbodyframe';

        if ( Validator.isString(par.dsc) ) {
            const dsc = document.createElement('div');
            dsc.className = 'dlgdsctext';
            dsc.innerHTML = Utils.escHtml(par.dsc);
            body_frame.appendChild(dsc);
        }

        ctt_frame.appendChild(hdr_frame);
		ctt_frame.appendChild(body_frame);

		const btn_frame = document.createElement('div');
		btn_frame.className = 'dlgbtnframe';

		const btn_close = document.createElement('div');
		btn_close.classList.add('dlgbtn');
		btn_close.classList.add('dlgclosebtn');
		btn_close.innerHTML = '<i class="fp fp-close-2"></i>';
		btn_frame.appendChild(btn_close);

        const filler = document.createElement('div');
		filler.className = 'filler';
		btn_frame.appendChild(filler);

        // do special rendering
        this.renderDlg(hdr_frame, body_frame, btn_frame);


		dlg_body.appendChild(ctt_frame)
		dlg_body.appendChild(btn_frame);
		dlg_wnd.appendChild(dlg_body);

		host.appendChild(dlg_wnd);

		const self = this;
		host.addEventListener('click', (e) => {
			self._onHostClick(e);
		});
		host.addEventListener('keyup', (e) => {
			self._onKeyStroke(e);
		}, true);
		btn_close.addEventListener('click', () => {
			self._onClose();
		});

    }

    /**
     * performs special dialog rendering
     * @param {HTMLElement} hdr_frame the header frame
     * @param {HTMLElement} body_frame the body frame
     * @param {HTMLElement} btn_frame  the button frame
     */
    renderDlg(hdr_frame, body_frame, btn_frame) {
        // nothing special here
    }

	/**
	 * shows the dialog
	 */
    show() {
        if ( this.hostElm ) {
            this.render();
            this._keysBlocked = KeyHdl.getInstance().isBlocked();
            KeyHdl.getInstance().blockGlobalKeyHandling(true);
            document.body.appendChild(this.hostElm);
        }
    }

    /**
     * closes this dialog
     */
    close() {
        this._onClose();
    }

    /**
     * handles the "Enter" key
     */
    onEnter() {
        // do nothing here
    }

	/**
	 * closes the dialog
	 */
	_onClose() {
		const self = this;
		setTimeout(() => {
			self.destroy();
		}, 1);
	}

	/**
	 * global "click" listener
	 * @param {MouseEvent} e the "click" event
	 */
    _onHostClick(e) {
        if ( (e.button === 0) && !HtmHelper.isChildOf(e.target, this.hostElm) ) {
            DomEventHelper.stopEvent(e);
        }
		if ( (e.button === 0) && (e.target === this.hostElm) ) {
			this._onClose();
		}
    }

	/**
	 * handles key strokes
	 * @param {KeyboardEvent} e the keyboard event
	 */
	_onKeyStroke(e) {
		let cls = false;
		switch ( e.key ) {
			case 'Enter':
				this.onEnter();
				cls = true;
				break;
			case 'Escape':
				cls = true;
				break;
			default:
				break;
		}
		if ( cls ) {
			this._onClose();
		}
	}
}